'use client';

import React from 'react';
import { PortableText } from '@portabletext/react';
import { motion } from 'framer-motion';
import Header from '@/components/global/partials/Header';
import Footer from '@/components/global/partials/Footer';
import { GroqImage } from '@/helpers/Image';
import { SanityVideoPlayer } from '@/components/ui/SanityVideoPlayer';
import {
  customRichTextComponent,
  richTextComponent,
} from '@/sanity/sanityPortableText';
import { PageSuccessStoriesSingleSchema } from '@/sanity/pages/PageSuccessStoriesSingleTemplate';
import RelatedArticles from '@/components/pageComponents/successStoriesSingle/RelatedArticles';

import Image from 'next/image';
import heroMobileBackground from '@/public/global/hero-mobile-background.png';
import heroDesktopBackground from '@/public/global/hero_desktop_background-lg-2.png';
import ScrollingBanner from '../pageComponents/global/ScrollingBanner';
import HeaderV2 from '../global/partials/HeaderV2';

const ease = [0.25, 0.1, 0.25, 1] as const;

const articleRichText = customRichTextComponent({
  types: {
    cornerList: ({ value }) => {
      const items = value?.items ?? [];
      return (
        <ul className="flex flex-col w-full p-0 list-none">
          {items.map((item: any, index: number) => (
            <li key={item._key ?? index}>
              <div className="flex gap-[3rem] items-center py-[3rem]">
                <div className="bg-[#003828] flex-shrink-0 w-[1rem] h-[1rem]" />
                <div className="flex-1 richtext richtext-start prose-p:mb-0 prose-p:text-[2rem] prose-p:font-medium prose-p:text-black/80 prose-p:leading-[1.7] prose-p:tracking-[-0.06rem]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </div>
              {index < items.length - 1 && (
                <hr className="m-0 border-t border-black/10" />
              )}
            </li>
          ))}
        </ul>
      );
    },
  },
});

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export default function PageSuccessStoriesSingleJSX({
  data,
}: {
  data: PageSuccessStoriesSingleSchema;
}) {
  return (
    <main className="flex flex-col items-center bg-white">
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType={'main'} // showBorder
          language={data.settings?.language}
        />
      )}

      {/* ── Hero ── */}
      <section className="relative w-full overflow-x-clip pt-[10rem] lg:pt-[16rem] pb-[4rem] lg:pb-[12rem]">
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-full top-[0rem] lg:hidden block z-0 pointer-events-none"
        >
          <Image
            src={heroMobileBackground}
            alt="Background"
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="absolute w-full h-[130rem] left-0 top-[0rem] hidden  lg:block z-0 pointer-events-none"
        >
          <Image
            src={heroDesktopBackground}
            alt="Background"
            className="w-full h-full object-cover object-center"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="relative z-10 mx-auto flex lg:w-[min(170.5rem,calc(100%-8rem))] w-[min(170.5rem,calc(100%-4rem))] flex-col items-center gap-[3rem]"
        >
          <div className="flex items-center gap-[2.5rem]">
            {data.category && (
              <span className="bg-[#0b6d51] font-geist text-[1.75rem] text-white px-[1.25rem] py-[0.625rem] rounded-[0.25rem] tracking-[-0.05rem] leading-[1.3]">
                {data.category}
              </span>
            )}
            {data.date && (
              <span className="font-geist text-[2.25rem] text-[#103037] opacity-50 tracking-[-0.07rem]">
                {formatDate(data.date)}
              </span>
            )}
          </div>

          {data.title && (
            <div className="richtext richtext-start max-w-[94.25rem] text-center prose-p:mb-0 prose-p:font-geist prose-p:font-medium prose-p:lg:text-[6rem] prose-p:text-[4.5rem] prose-headings:lg:text-[6rem] prose-headings:text-[4.5rem] prose-p:text-black prose-p:leading-[1.2] prose-p:tracking-[-0.18rem] prose-strong:font-bold">
              <PortableText value={data.title} components={richTextComponent} />
            </div>
          )}
        </motion.div>
      </section>

      {/* ── Hero Media (video takes priority over image) ── */}
      {((!!(data.video?.muxVideo?.playbackId || data.video?.embed || data.video?.embedUrl)) || data.mainImage) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease, delay: 0.2 }}
          className="lg:w-[min(140rem,calc(100%-8rem))] w-[min(140rem,calc(100%-4rem))] mx-auto"
        >
          <div
            className={`relative w-full rounded-[0.25rem] overflow-hidden aspect-video`}
          >
            {(data.video?.muxVideo?.playbackId || data.video?.embed || data.video?.embedUrl) ? (
              <div className="absolute inset-0">
                <SanityVideoPlayer
                  playerData={data.video}
                  className="!rounded-[0.25rem]"
                />
              </div>
            ) : (
              <GroqImage
                src={data.mainImage!.src}
                alt={data.mainImage!.name || ''}
                fill
                className="object-cover"
              />
            )}
          </div>
        </motion.div>
      )}

      {/* ── Article Body ── */}
      {data.body && (
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="mx-auto w-[min(94.25rem,calc(100%-4rem))] lg:w-[min(94.25rem,calc(100%-8rem))] pb-[7rem] pt-[4rem] lg:py-[11rem]"
        >
          <div className="richtext richtext-start flex flex-col gap-[3.5rem] lg:gap-[4.5rem] prose-headings:mb-0 prose-headings:font-medium prose-headings:text-[#2e3436] prose-headings:leading-[1.16] prose-h2:text-[4.5rem] prose-h3:text-[3.5rem] prose-h4:text-[2.75rem] prose-p:mb-0 prose-p:w-full prose-p:text-[2.25rem] prose-p:text-[#1c1f22] prose-p:leading-[1.6] prose-strong:font-bold">
            <PortableText value={data.body} components={articleRichText} />
          </div>
        </motion.section>
      )}

      {!!data?.relatedArticles?.length && (
        <RelatedArticles
          articles={data.relatedArticles}
          language={data.settings?.language}
        />
      )}

      <ScrollingBanner
        className="bg-[#f4f5f5] border-y-0"
        words={data.scrollingWords?.words}
        wordsI18n={data.scrollingWords?.wordsI18n}
      />
      {data?.settings?.footer && <Footer data={data.settings.footer} language={data?.settings?.language} />}
    </main>
  );
}
